<template>
  <div class="session-dashboard">
    <v-container fluid>
      <!-- Header -->
      <v-row class="mb-6">
        <v-col>
          <div class="d-flex align-center justify-space-between">
            <div>
              <h1 class="text-h4 font-weight-bold mb-2">Dashboard de Sesión</h1>
              <p class="text-body-1 text-medium-emphasis">
                Gestiona tu sesión de quiz en tiempo real
              </p>
            </div>
            <v-btn
              color="primary"
              variant="outlined"
              prepend-icon="mdi-arrow-left"
              @click="$router.push('/pins')"
            >
              Volver a PINs
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <!-- Session Status Card -->
      <v-row class="mb-6">
        <v-col cols="12">
          <SessionStatus />
        </v-col>
      </v-row>

      <!-- Action Cards -->
      <v-row class="mb-6">
        <v-col cols="12" md="6">
          <SessionControls
            @quiz-started="handleQuizStarted"
            @session-completed="handleQuizCompleted"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-card>
            <v-card-title>
              <v-icon left>mdi-chart-box-outline</v-icon>
              Estadísticas Rápidas
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-column ga-3">
                <div class="d-flex justify-space-between align-center">
                  <span class="text-body-2">Total de participantes</span>
                  <v-chip color="primary" size="small">{{ participants.length }}</v-chip>
                </div>
                <div class="d-flex justify-space-between align-center">
                  <span class="text-body-2">En espera</span>
                  <v-chip color="orange" size="small">{{ waitingParticipants }}</v-chip>
                </div>
                <div class="d-flex justify-space-between align-center">
                  <span class="text-body-2">Activos</span>
                  <v-chip color="success" size="small">{{ activeParticipants }}</v-chip>
                </div>
                <div class="d-flex justify-space-between align-center">
                  <span class="text-body-2">Completados</span>
                  <v-chip color="info" size="small">{{ completedParticipants }}</v-chip>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Ver Pregunta Button (shown when quiz started and modal closed) -->
      <v-row v-if="showViewQuestionButton" class="mb-6">
        <v-col cols="12" class="text-center">
          <v-btn
            color="primary"
            size="large"
            prepend-icon="mdi-comment-question"
            @click="openQuestionModal"
          >
            Ver Pregunta
          </v-btn>
        </v-col>
      </v-row>

      <!-- Ver Pregunta Finalizada & Download Report Buttons (shown when session completed and modal closed) -->
      <v-row v-if="showViewCompletedQuestionButton" class="mb-6">
        <v-col cols="12" class="text-center">
          <div class="d-flex justify-center gap-3 flex-wrap">
            <v-btn
              color="success"
              size="large"
              prepend-icon="mdi-check-circle"
              @click="openQuestionDetailModal"
            >
              Ver Pregunta Finalizada
            </v-btn>
            <v-btn
              color="primary"
              size="large"
              prepend-icon="mdi-file-pdf-box"
              :loading="downloadingReport"
              @click="downloadReport"
            >
              Descargar Reporte
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <!-- Participants List -->
      <v-row>
        <v-col cols="12">
          <WaitingStudentsList />
        </v-col>
      </v-row>
    </v-container>

    <!-- Error snackbar -->
    <v-snackbar v-model="showError" color="error" timeout="5000" location="top right">
      <v-icon left>mdi-alert-circle</v-icon>
      {{ error }}
    </v-snackbar>

    <!-- Success snackbar -->
    <v-snackbar v-model="showSuccess" color="success" timeout="3000" location="top right">
      <v-icon left>mdi-check-circle</v-icon>
      {{ successMessage }}
    </v-snackbar>

    <!-- Question Modal (During Active Session) -->
    <v-dialog v-model="showQuestionModal" max-width="1200px">
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon class="mr-2">mdi-file-document-outline</v-icon>
            Pregunta
          </div>
          <v-btn icon variant="text" @click="closeQuestionModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <SessionQuestion
            v-if="schoolGradeId && showQuestionModal"
            :key="`session-question-${route.params.pinCode}-${schoolGradeId}`"
            :school-grade-id="schoolGradeId"
          />
          <v-alert v-else type="info" variant="tonal">
            <v-icon>mdi-information</v-icon>
            No hay grado escolar asignado a esta sesión.
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Question Detail Modal (After Session Completed) -->
    <v-dialog v-model="showQuestionDetailModal" max-width="1200px" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon class="mr-2">mdi-file-document-outline</v-icon>
            Pregunta Finalizada
          </div>
          <v-btn icon variant="text" @click="closeQuestionDetailModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <QuestionDetail
            v-if="currentQuestionId && showQuestionDetailModal"
            :key="`question-detail-${currentQuestionId}`"
            :question-id="currentQuestionId"
            :hide-actions="true"
          />
          <v-alert v-else type="info" variant="tonal">
            <v-icon>mdi-information</v-icon>
            No hay pregunta disponible para mostrar.
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useWebSocket } from '@/composables/useWebSocket'
import { reportsApi } from '@/services/reportsApi'
import SessionStatus from '@/components/session/SessionStatus.vue'
import SessionControls from '@/components/session/SessionControls.vue'
import WaitingStudentsList from '@/components/session/WaitingStudentsList.vue'
import SessionQuestion from '@/components/session/SessionQuestion.vue'
import QuestionDetail from '@/components/questions/QuestionDetail.vue'

const route = useRoute()
const router = useRouter()
const store = useStore()
const { connect, disconnect, listenToTeacherSession, isConnected: wsConnected } = useWebSocket()

// Reactive data
const showError = ref(false)
const showSuccess = ref(false)
const successMessage = ref('')
const pollingInterval = ref(null)
const websocketChannel = ref(null)
const showQuestionModal = ref(false)
const quizHasStarted = ref(false)
const showQuestionDetailModal = ref(false)
const sessionHasCompleted = ref(false)
const downloadingReport = ref(false)

// Computed properties
const participants = computed(() => store.getters['session/getParticipants'])
const error = computed(() => store.getters['session/getError'])
const loading = computed(() => store.getters['session/isLoading'])
const schoolGradeId = computed(() => store.getters['session/getSchoolGradeId'])
const currentQuestionId = computed(() => store.getters['session/getCurrentQuestionId'])

const waitingParticipants = computed(
  () => participants.value.filter((p) => p.status === 'waiting').length,
)

const activeParticipants = computed(
  () => participants.value.filter((p) => p.status === 'active').length,
)

const completedParticipants = computed(
  () => participants.value.filter((p) => p.status === 'completed').length,
)

const sessionStatus = computed(() => store.getters['session/getSessionStatus'])

const showViewQuestionButton = computed(() => {
  return quizHasStarted.value && !showQuestionModal.value && sessionStatus.value !== 'completed'
})

const showViewCompletedQuestionButton = computed(() => {
  return sessionHasCompleted.value && !showQuestionDetailModal.value && currentQuestionId.value
})

// Watch for errors
const unwatchError = computed(() => {
  if (error.value && !showError.value) {
    showError.value = true
  }
  return error.value
})

// Methods
const loadSessionData = async () => {
  const pinCode = route.params.pinCode

  if (!pinCode) {
    router.push('/pins')
    return
  }

  const result = await store.dispatch('session/getSessionDetails', pinCode)
  if (!result.success) {
    // Handle error - maybe redirect to pins if session not found
    setTimeout(() => {
      if (result.error.includes('not found')) {
        router.push('/pins')
      }
    }, 3000)
  }
}

const startPolling = () => {
  if (pollingInterval.value) return

  pollingInterval.value = setInterval(async () => {
    await loadSessionData()
  }, 10000) // Poll every 10 seconds for teacher dashboard

  store.dispatch('session/setPolling', true)
}

const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value)
    pollingInterval.value = null
    store.dispatch('session/setPolling', false)
  }
}

const showSuccessMessage = (message) => {
  successMessage.value = message
  showSuccess.value = true
}

const openQuestionModal = () => {
  showQuestionModal.value = true
}

const closeQuestionModal = () => {
  showQuestionModal.value = false
}

const handleQuizStarted = () => {
  quizHasStarted.value = true
  showQuestionModal.value = true
}

const handleQuizCompleted = () => {
  quizHasStarted.value = false
  showQuestionModal.value = false
  sessionHasCompleted.value = true

  // Auto-open the question detail modal when session completes
  if (currentQuestionId.value) {
    showQuestionDetailModal.value = true
  }
}

const openQuestionDetailModal = () => {
  showQuestionDetailModal.value = true
}

const closeQuestionDetailModal = () => {
  showQuestionDetailModal.value = false
}

const downloadReport = async () => {
  const pinCode = route.params.pinCode
  if (!pinCode) {
    showError.value = true
    return
  }

  downloadingReport.value = true

  try {
    // Call API to download PDF report
    const blob = await reportsApi.downloadSessionReportPDF({ pin_code: pinCode })

    // Create blob URL
    const url = window.URL.createObjectURL(blob)

    // Create temporary link and click it
    const link = document.createElement('a')
    link.href = url
    link.download = `reporte-sesion-${pinCode}-${Date.now()}.pdf`
    document.body.appendChild(link)
    link.click()

    // Cleanup
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    showSuccessMessage('Reporte descargado exitosamente')
  } catch (err) {
    console.error('Error downloading report:', err)
    showError.value = true
  } finally {
    downloadingReport.value = false
  }
}

const setupWebSocket = () => {
  const pinCode = route.params.pinCode
  if (!pinCode) return

  try {
    // Connect to WebSocket if not already connected
    if (!wsConnected.value) {
      connect()
    }

    // Listen to teacher session events
    websocketChannel.value = listenToTeacherSession(pinCode)

    if (websocketChannel.value) {
      console.log(`Teacher WebSocket listening to session: ${pinCode}`)
    } else {
      console.log('Teacher WebSocket not available, using polling fallback')
    }
  } catch (error) {
    console.error('Failed to setup teacher WebSocket:', error)
  }
}

// Lifecycle hooks
onMounted(async () => {
  await loadSessionData()

  // Check if session is already completed on load
  if (sessionStatus.value === 'completed') {
    sessionHasCompleted.value = true
  }

  // Setup WebSocket connection
  setupWebSocket()

  // Start polling as fallback
  startPolling()
})

onUnmounted(() => {
  stopPolling()

  // Leave WebSocket channel
  if (websocketChannel.value && route.params.pinCode) {
    const channelName = `quiz-session.${route.params.pinCode}.teacher`
    // Note: The websocket service will handle cleanup
  }

  store.dispatch('session/clearSession')
})

// Provide method to child components
defineExpose({
  showSuccessMessage,
})
</script>

<style scoped>
.session-dashboard {
  background-color: #f5f5f5;
  min-height: 100vh;
  padding: 2rem 0;
}

.v-container {
  max-width: 1400px;
}

.gap-3 {
  gap: 12px;
}

@media (max-width: 768px) {
  .session-dashboard {
    padding: 1rem 0;
  }

  .text-h4 {
    font-size: 1.5rem !important;
  }
}
</style>
